import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Tag, Percent, LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTouchGestures } from '@/hooks/useTouchGestures';
import { useIsMobile } from '@/hooks/use-mobile';

interface Category {
  id: string;
  name: string;
  count?: number;
}

interface HorizontalCategoryFilterProps {
  categories?: Category[];
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  isLoading?: boolean;
}

const HorizontalCategoryFilter = ({
  categories = [],
  selectedCategory,
  setSelectedCategory,
  isLoading = false
}: HorizontalCategoryFilterProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const isMobile = useIsMobile();

  // Special filter options
  const specialFilters: Array<{ id: string; name: string; icon: LucideIcon; count: number | null }> = [
    { id: 'all', name: 'All Categories', icon: Tag, count: categories?.reduce((acc, cat) => acc + (cat.count || 0), 0) || 0 },
    { id: 'sale', name: 'Sale', icon: Percent, count: null }
  ];

  const allFilters = [...specialFilters, ...categories];

  // Touch gestures for mobile swiping
  const touchGestures = useTouchGestures({
    onSwipeLeft: () => scrollRight(),
    onSwipeRight: () => scrollLeft(),
    minSwipeDistance: 50
  });

  const checkScrollButtons = () => {
    if (!scrollRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    checkScrollButtons();
    const handleResize = () => checkScrollButtons();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [categories]);

  const scrollLeft = () => {
    if (!scrollRef.current) return;
    const scrollAmount = isMobile ? 200 : 300;
    scrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (!scrollRef.current) return;
    const scrollAmount = isMobile ? 200 : 300;
    scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const scrollToCategory = (categoryId: string) => {
    if (!scrollRef.current) return;
    
    const categoryElement = scrollRef.current.querySelector(`[data-category-id="${categoryId}"]`) as HTMLElement;
    if (categoryElement) {
      const container = scrollRef.current;
      const elementLeft = categoryElement.offsetLeft;
      const elementWidth = categoryElement.offsetWidth;
      const containerWidth = container.clientWidth;
      
      const scrollTo = elementLeft - (containerWidth / 2) + (elementWidth / 2);
      container.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    if (isDragging) return;
    setSelectedCategory(categoryId);
    scrollToCategory(categoryId);
  };

  const handleMouseDown = () => setIsDragging(false);
  const handleMouseMove = () => setIsDragging(true);

  if (isLoading) {
    return (
      <div className="relative py-6">
        <div className="flex gap-3 animate-pulse">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-10 w-24 bg-muted/50 rounded-pill flex-shrink-0"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative py-6 group">
      {/* Left Arrow */}
      {!isMobile && showLeftArrow && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={scrollLeft}
            className="h-10 w-10 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 shadow-luxury-feature hover:bg-background/95 hover:shadow-luxury-hover transition-all duration-300"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </motion.div>
      )}

      {/* Right Arrow */}
      {!isMobile && showRightArrow && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={scrollRight}
            className="h-10 w-10 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 shadow-luxury-feature hover:bg-background/95 hover:shadow-luxury-hover transition-all duration-300"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </motion.div>
      )}

      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        className={`flex gap-3 overflow-x-auto scrollbar-hide px-4 ${isMobile ? 'px-2' : 'px-12'}`}
        onScroll={checkScrollButtons}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        {...(isMobile ? touchGestures : {})}
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {allFilters.map((filter) => {
          const isActive = selectedCategory === filter.id;
          const IconComponent = 'icon' in filter ? filter.icon as LucideIcon : null;
          
          return (
            <motion.button
              key={filter.id}
              data-category-id={filter.id}
              onClick={() => handleCategorySelect(filter.id)}
              className={`
                relative flex-shrink-0 px-6 py-2.5 rounded-pill font-medium text-sm transition-all duration-300 cursor-pointer select-none
                ${isMobile ? 'px-4 py-2 text-xs' : ''}
                ${isActive 
                  ? 'bg-gradient-luxury-cta text-foreground shadow-luxury-feature border border-luxury-gold/30' 
                  : 'bg-background/60 backdrop-blur-sm text-muted-foreground hover:text-foreground border border-border/30 hover:border-border/60 hover:bg-background/80'
                }
              `}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ scrollSnapAlign: 'start' }}
            >
              <div className="flex items-center gap-2">
                {IconComponent && (
                  <IconComponent className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${isActive ? 'text-luxury-gold' : ''}`} />
                )}
                <span className="whitespace-nowrap">
                  {filter.name}
                </span>
                {filter.count !== null && filter.count !== undefined && (
                  <span className={`
                    ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold
                    ${isActive 
                      ? 'bg-luxury-gold/20 text-luxury-gold-dark' 
                      : 'bg-muted/60 text-muted-foreground'
                    }
                  `}>
                    {filter.count}
                  </span>
                )}
              </div>

              {/* Active indicator */}
              {isActive && (
                <motion.div
                  layoutId="activeCategory"
                  className="absolute inset-0 bg-gradient-luxury-cta rounded-pill -z-10 opacity-20"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Fade edges for mobile */}
      {isMobile && (
        <>
          <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-background via-background/60 to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-background via-background/60 to-transparent pointer-events-none" />
        </>
      )}
    </div>
  );
};

export default HorizontalCategoryFilter;